<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, sameAs } from '@vuelidate/validators';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import { frontendURL } from 'dashboard/helper/URLHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const currentUser = useMapGetter('getCurrentUser');

const currentPassword = ref('');
const password = ref('');
const passwordConfirmation = ref('');
const isSubmitting = ref(false);

const rules = {
  currentPassword: { required },
  password: { required, minLength: minLength(6) },
  passwordConfirmation: {
    required,
    sameAsPassword: sameAs(password),
  },
};

const v$ = useVuelidate(rules, {
  currentPassword,
  password,
  passwordConfirmation,
});

const isButtonDisabled = computed(
  () => v$.value.$invalid || isSubmitting.value
);

const submit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    useAlert(t('FORCE_PASSWORD_RESET.FORM.ERROR'));
    return;
  }

  isSubmitting.value = true;
  try {
    await store.dispatch('updatePassword', {
      currentPassword: currentPassword.value,
      password: password.value,
      passwordConfirmation: passwordConfirmation.value,
    });
    useAlert(t('FORCE_PASSWORD_RESET.SUCCESS'));
    const accountId = route.params.accountId || currentUser.value.account_id;
    router.replace(frontendURL(`accounts/${accountId}/dashboard`));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) || t('FORCE_PASSWORD_RESET.FORM.API_ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="flex items-center justify-center w-full h-full bg-n-slate-2">
    <div class="w-full max-w-md p-8 bg-n-background shadow rounded-xl">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ $t('FORCE_PASSWORD_RESET.TITLE') }}
      </h1>
      <p class="mt-2 text-sm text-n-slate-11">
        {{ $t('FORCE_PASSWORD_RESET.DESCRIPTION') }}
      </p>

      <form class="flex flex-col gap-4 mt-6" @submit.prevent="submit">
        <woot-input
          v-model="currentPassword"
          type="password"
          :class="{ error: v$.currentPassword.$error }"
          :label="$t('FORCE_PASSWORD_RESET.FORM.CURRENT_PASSWORD.LABEL')"
          :placeholder="
            $t('FORCE_PASSWORD_RESET.FORM.CURRENT_PASSWORD.PLACEHOLDER')
          "
          :error="
            v$.currentPassword.$error
              ? $t('FORCE_PASSWORD_RESET.FORM.CURRENT_PASSWORD.ERROR')
              : ''
          "
          @input="v$.currentPassword.$touch"
          @blur="v$.currentPassword.$touch"
        />

        <woot-input
          v-model="password"
          type="password"
          :class="{ error: v$.password.$error }"
          :label="$t('FORCE_PASSWORD_RESET.FORM.PASSWORD.LABEL')"
          :placeholder="$t('FORCE_PASSWORD_RESET.FORM.PASSWORD.PLACEHOLDER')"
          :error="
            v$.password.$error
              ? $t('FORCE_PASSWORD_RESET.FORM.PASSWORD.ERROR')
              : ''
          "
          @input="v$.password.$touch"
          @blur="v$.password.$touch"
        />

        <woot-input
          v-model="passwordConfirmation"
          type="password"
          :class="{ error: v$.passwordConfirmation.$error }"
          :label="$t('FORCE_PASSWORD_RESET.FORM.PASSWORD_CONFIRMATION.LABEL')"
          :placeholder="
            $t('FORCE_PASSWORD_RESET.FORM.PASSWORD_CONFIRMATION.PLACEHOLDER')
          "
          :error="
            v$.passwordConfirmation.$error
              ? $t('FORCE_PASSWORD_RESET.FORM.PASSWORD_CONFIRMATION.ERROR')
              : ''
          "
          @input="v$.passwordConfirmation.$touch"
          @blur="v$.passwordConfirmation.$touch"
        />

        <div class="mt-2">
          <NextButton
            type="submit"
            class="w-full"
            :label="$t('FORCE_PASSWORD_RESET.FORM.SUBMIT')"
            :disabled="isButtonDisabled"
            :is-loading="isSubmitting"
          />
        </div>
      </form>
    </div>
  </div>
</template>
